import { NavLink, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarHeart,
  Users,
  CalendarDays,
  BookText,
  Settings as SettingsIcon,
  LifeBuoy,
  LogOut,
  Clock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import BrandWordmark from "@/components/BrandWordmark";
import { resolveLogoUrl } from "@/lib/api";

export default function Sidebar() {
  const { user, logout, isTrialActive, trialDaysLeft } = useAuth();
  const { t, language } = useTheme();
  const en = language === "en";
  const isPremium = user?.plan === "premium";
  const isCollaborator = user?.is_collaborator || user?.role === "collaborator";
  const location = useLocation();
  const logoSrc = resolveLogoUrl(user?.logo_url);
  const initials = (user?.agency_name || user?.name || "")
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // I collaboratori (accesso condiviso) vedono solo Eventi + Guida.
  const links = isCollaborator
    ? [
        { to: "/events", label: t("nav.events"), icon: CalendarHeart, testid: "nav-events" },
        { to: "/guida",  label: t("nav.guide"),  icon: LifeBuoy,      testid: "nav-guide" },
      ]
    : [
        { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard", tourStep: "nav-dashboard" },
        { to: "/events",    label: t("nav.events"),    icon: CalendarHeart,   testid: "nav-events",    tourStep: "nav-events" },
        { to: "/clients",   label: t("nav.clients"),   icon: Users,           testid: "nav-clients",   tourStep: "nav-clients" },
        { to: "/calendar",  label: t("nav.calendar"),  icon: CalendarDays,    testid: "nav-calendar",  tourStep: "nav-calendar" },
        { to: "/suppliers", label: t("nav.suppliers"), icon: BookText,        testid: "nav-suppliers", tourStep: "nav-suppliers" },
        { to: "/guida",     label: t("nav.guide"),     icon: LifeBuoy,        testid: "nav-guide" },
        { to: "/settings",  label: t("nav.settings"),  icon: SettingsIcon,    testid: "nav-settings",  tourStep: "nav-settings" },
      ];

  return (
    <aside
      className="hidden md:flex w-64 shrink-0 flex-col border-r border-border bg-card h-screen sticky top-0"
      data-testid="app-sidebar"
    >
      <div className="px-7 pt-8 pb-6">
        <BrandWordmark size="sm" showPro={user?.plan === "premium"} data-testid="sidebar-brand-wordmark" />
        <div className="flex items-center gap-2.5 mt-3">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={user?.agency_name || "logo"}
              className="w-9 h-9 rounded-full object-cover border border-border bg-muted/40 shrink-0"
              data-testid="sidebar-agency-logo"
            />
          ) : initials ? (
            <div
              className="w-9 h-9 rounded-full border border-border bg-muted/40 flex items-center justify-center text-[11px] tracking-wider text-muted-foreground shrink-0"
              data-testid="sidebar-agency-initials"
            >
              {initials}
            </div>
          ) : null}
          {user?.agency_name ? (
            <div className="label-eyebrow min-w-0 truncate" data-testid="sidebar-agency-name">
              {user.agency_name}
            </div>
          ) : null}
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-0.5">
        {links.map((l) => {
          const Icon = l.icon;
          const active = location.pathname.startsWith(l.to);
          return (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              {...(l.tourStep ? { "data-tour-step": l.tourStep } : {})}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-sm text-[19px] transition-all duration-300 ${
                active
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <Icon className="w-[18px] h-[18px] stroke-[1.4]" />
              <span className="tracking-wide">{l.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Trial banner */}
      {isTrialActive && (
        <div className={`mx-3 mb-2 rounded-sm border p-3 space-y-1.5 ${
          trialDaysLeft <= 2
            ? "border-destructive/40 bg-destructive/5"
            : "border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-700/40"
        }`}>
          <div className={`flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.12em] ${
            trialDaysLeft <= 2 ? "text-destructive" : "text-amber-800 dark:text-amber-300"
          }`}>
            <Clock className="w-3 h-3" />
            {isPremium
              ? (en ? "Premium trial" : "Prova Premium")
              : (en ? "Free trial" : "Prova gratuita")}
          </div>
          <div className={`text-[11px] ${trialDaysLeft <= 2 ? "text-destructive" : "text-amber-800 dark:text-amber-300"}`}>
            {trialDaysLeft === 1
              ? (en ? "Last day" : "Ultimo giorno")
              : (en ? `${trialDaysLeft} days left` : `${trialDaysLeft} giorni rimasti`)}
          </div>
          <Link
            to="/settings"
            className={`block text-[10px] uppercase tracking-[0.15em] hover:underline ${
              trialDaysLeft <= 2 ? "text-destructive/80" : "text-amber-700 dark:text-amber-400"
            }`}
          >
            {isPremium
              ? (en ? "Manage subscription →" : "Gestisci abbonamento →")
              : (en ? "Activate plan →" : "Attiva un piano →")}
          </Link>
        </div>
      )}

      <div className="px-5 pb-6 pt-4 border-t border-border space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm truncate">{user?.name}</div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              {isCollaborator
                ? (en ? "Collaborator" : "Collaboratore")
                : isPremium
                  ? (isTrialActive ? (en ? "Premium · trial" : "Premium · in prova") : "Premium")
                  : "Base"}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="p-2 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={t("nav.logout")}
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
